import { Button, Form } from "react-bootstrap";
import { SubtaskIcon } from "../icons";
import { useRef, useState, useCallback, useEffect, useContext } from "react";
import { SubtaskListItem, AddSubtaskInput, ListProgressBar } from "./";
import { useDetectOutsideDoubleClick } from "../../hooks/useDetectOutsideDoubleClick";
import { Popup } from "../Popup";
import { useDispatch, useSelector } from "react-redux";
import { listActions } from "../../actions/list.actions";
import { ContentGroupContext } from "../../providers/ContentGroupProvider";
import { buildFormDataFromObject } from "../../utils/commonUtils";
import { Draggable, Droppable } from "react-beautiful-dnd";

function SubtaskList({ 
  list, 
  provided,
  index,
  readOnly = false 
}) {
  const dispatch = useDispatch();
  const [subtaskTitle, setSubtaskTitle] = useState(list?.title);
  const initialLoaded = useRef(false);
  const inputRef = useRef();
  const [showDeletePopup, setShowDeletePopup] = useState(false);
  const { workspace_id, content_group_id } = useContext(ContentGroupContext);
  const [hidden, setHidden] = useState(list?.hidden || false);

  const canCreate = useSelector(state => state.list.can_create);
  const canUpdate = useSelector(state => state.list.can_update);
  const canDestroy = useSelector(state => state.list.can_destroy);

  const enableContentEditable = useCallback(() => {
    setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  const { doubleClick, setIsActive, isActive } = useDetectOutsideDoubleClick(enableContentEditable, inputRef);

  const handleClosePopup = () => setShowDeletePopup(false);

  const handleDelete = () => {
    dispatch(listActions.destroyList({
      workspace_id,
      content_group_id,
      list_id: list?.id,
      onEnd: handleClosePopup
    }));
  };

  const handleSubmit = async (formData, options = {}) => {
    return dispatch(listActions.updateList({
      formData,
      workspace_id,
      content_group_id,
      list_id: list?.id,
      ...options
    }));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setIsActive(false);
    }
  };

  const handleVisibilityUpdate = () => {
    const visibility = !hidden;
    let formData = new FormData();
    formData.hidden =  visibility;

    buildFormDataFromObject(formData, { hidden: visibility }, "list");
    setHidden(visibility);

    handleSubmit(formData, {
      onError: () => setHidden(!visibility)
    });
  };

  useEffect(() => {
    if (!isActive && initialLoaded.current) {
      let formData = new FormData();
      formData.title =  inputRef.current.value;
      
      buildFormDataFromObject(formData, { title: inputRef.current.value }, "list");

      handleSubmit(formData, {
        onError: () => {
          setSubtaskTitle(list?.title);
          inputRef.current.value = list?.title;
        }
      });
    }
    initialLoaded.current = true;
  }, [isActive]);

  useEffect(() => {
    if (list && subtaskTitle !== list?.title) {
      setSubtaskTitle(list?.title);
    }
  }, [list]);

  return (
    <Draggable 
      draggableId={`list-${list.id}`} 
      index={index}
      isDragDisabled={canUpdate ? false : true}
    >
      {(provided, snapshot) => (  
        <div 
          className={`subtask mb-3 pb-3${snapshot.isDragging ? ' subtask--dragging' : ''}`}
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
        >
          {showDeletePopup && (
            <Popup
              handleClose={handleClosePopup}
              handleDelete={handleDelete}
              display_text="Are you sure?"
            />
          )}

          <div className={`subtask-form mb-1 ${isActive ? 'subtask-form-input_active' : 'subtask-form-title'}`}>
            <div className="d-flex align-items-center">
              <div className="d-flex align-items-center w-100">
                <div className="subtask-form-icon d-flex align-items-center">
                  <SubtaskIcon />
                </div>
                <div className="d-flex align-items-center justify-content-between w-100">
                  <Form.Control
                    placeholder="Add List Title"
                    ref={inputRef}
                    className={`${isActive ? '' : 'd-none'} h-100 subtask-title w-100`}
                    value={subtaskTitle}
                    onChange={e => setSubtaskTitle(e.target.value)}
                    onKeyPress={canUpdate && !readOnly ? handleKeyPress : null}
                    type="text"
                  />
                  <div
                    className={`h-100 subtask-title w-100 ${isActive ? 'd-none' : ''}`}
                    onClick={canUpdate && !readOnly ? doubleClick : null}
                  >
                    {subtaskTitle}
                  </div>
                </div>
              </div>
              {!isActive && (canUpdate || canDestroy) && !readOnly && (
                <div className="d-flex justify-content-end subtask-buttons">
                  {canUpdate && (
                    <Button
                      variant="subtask"
                      className="btn-subtask-visibility"
                      onClick={handleVisibilityUpdate}
                    >
                      {hidden ? 'Show ' : 'Hide '}to Guests
                    </Button>
                  )}
                  {canDestroy && (
                    <Button
                      variant="subtask"
                      className="btn-subtask-delete"
                      onClick={() => setShowDeletePopup(true)}
                    >
                      Delete
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>

          <ListProgressBar list={list} />
          <Droppable droppableId={`list-${list.id}`} type="ITEM">
             {(itemProvided) => (
                <div
                  ref={itemProvided.innerRef}
                  {...itemProvided.droppableProps}
                >
                  {list?.items.map((item, index) => (
                    <SubtaskListItem
                      item={item}
                      index={index}
                      key={`list_item_${item.id}`}
                      readOnly={readOnly}
                    />
                  ))}
                  {itemProvided.placeholder}
                </div>
              )}
          </Droppable>
          {canCreate && !readOnly && <AddSubtaskInput list={list} isDragging={snapshot.isDragging} />}
        </div>
      )}
    </Draggable>
  );
}

export { SubtaskList };
